import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Play,
  Stop,
  Pause,
  ArrowClockwise,
  Fish,
  Waveform,
  Warning,
  CheckCircle,
  Timer,
  LinkSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { logError } from "@/lib/log";
import { useAuth } from "@/lib/auth.jsx";
import { accountStatusView, formatWait } from "@/lib/accountStatus";

const STATUS_STYLES = {
  idle: { color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", dot: "bg-slate-500" },
  starting: { color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30", dot: "bg-cyan-500" },
  opening: { color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30", dot: "bg-cyan-500" },
  joining: { color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30", dot: "bg-cyan-500" },
  waiting: { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30", dot: "bg-yellow-500" },
  fishing: { color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30", dot: "bg-green-500" },
  extracting: { color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/30", dot: "bg-pink-500" },
  selling: { color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/30", dot: "bg-pink-500" },
  inventory: { color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/30", dot: "bg-pink-500" },
  favoriting: { color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/30", dot: "bg-pink-500" },
  verifying: { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30", dot: "bg-orange-500" },
  paused: { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30", dot: "bg-yellow-500" },
  stopped: { color: "text-slate-500", bg: "bg-slate-500/10", border: "border-slate-500/30", dot: "bg-slate-500" },
  error: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", dot: "bg-red-500" },
};

function formatSince(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

export default function Status() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [tg, setTg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [receivedAt, setReceivedAt] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const loading = useRef(false);

  const load = useCallback(async () => {
    if (loading.current) return;
    loading.current = true;
    try {
      const [s, t] = await Promise.all([
        api.get("/automation/status"),
        api.get("/telegram/status"),
      ]);
      setStatus(s.data);
      setTg(t.data);
      setReceivedAt(Date.now());
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
      logError("Gagal memuat status:", e);
    } finally {
      loading.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(clock); };
  }, [load]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/automation/${path}`);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const s = status?.status || "idle";
  const view = accountStatusView(status, tg, now, receivedAt || now);
  const style = STATUS_STYLES[view.tone] || STATUS_STYLES.idle;
  const isActive = !["idle", "stopped", "paused", "error"].includes(s);
  const isAdmin = user?.role === "admin";
  const lastMessage = /FloodWait|Traceback|Timeout|browser_|ERR_|PermissionError|SendMessageRequest/i.test(status?.last_message || "")
    ? view.description : status?.last_message || "—";

  return (
    <div className="space-y-6 md:space-y-8" data-testid="status-page">
      <div className="flex items-start justify-between gap-5 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-pink-300 mb-2">Ringkasan akun</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Status bot</h1>
          <p className="mt-2 text-sm text-slate-400">Pantau sesi dan kendalikan automation dari satu tempat.</p>
        </div>
        <div className="flex w-full sm:w-auto gap-2 flex-wrap">
          {!isActive && s !== "paused" && (
            <Button
              onClick={() => act("start", "Automation dimulai")}
              disabled={busy || view.blockStart}
              data-testid="btn-start"
              className="h-11 flex-1 sm:flex-none bg-pink-400 hover:bg-pink-300 text-slate-950 font-bold rounded-xl"
            >
              <Play size={16} weight="fill" className="mr-2" /> Mulai bot
            </Button>
          )}
          {isActive && (
            <Button
              onClick={() => act("pause", "Dijeda")}
              disabled={busy}
              data-testid="btn-pause"
              variant="outline"
              className="h-11 flex-1 sm:flex-none border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-xl"
            >
              <Pause size={16} className="mr-2" /> Jeda
            </Button>
          )}
          {s === "paused" && (
            <Button
              onClick={() => act("resume", "Dilanjutkan")}
              disabled={busy || view.blockStart}
              data-testid="btn-resume"
              className="h-11 flex-1 sm:flex-none bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold rounded-xl"
            >
              <ArrowClockwise size={16} className="mr-2" /> Lanjutkan
            </Button>
          )}
          {(isActive || s === "paused") && (
            <Button
              onClick={() => act("stop", "Automation dihentikan")}
              disabled={busy}
              data-testid="btn-stop"
              variant="outline"
              className="h-11 flex-1 sm:flex-none border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-xl"
            >
              <Stop size={16} weight="fill" className="mr-2" /> Hentikan
            </Button>
          )}
        </div>
      </div>

      {/* State first; numbers stay scannable below it on small screens. */}
      <div className="dashboard-status-panel rounded-3xl p-5 sm:p-7" data-testid="status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className={`mt-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${style.bg} border ${style.border}`}>
              <span className={`h-3 w-3 rounded-full ${style.dot} ${isActive ? "dot-pulse" : ""}`} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{loadFailed ? "Status terakhir" : "Kondisi saat ini"}</div>
              <h2 className={`mt-1 font-heading text-2xl sm:text-3xl font-bold ${style.color}`} data-testid="account-status-label">{view.label}</h2>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed" data-testid="account-status-description">{view.description}</p>
              {view.remainingSeconds !== null && (
                <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2 text-sm text-slate-100" data-testid="account-wait-badge">
                  <span>{view.countdownLabel}:</span>
                  <strong className="font-mono text-cyan-300">{formatWait(view.remainingSeconds)}</strong>
                </div>
              )}
            </div>
          </div>
          <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[11px] font-bold tracking-wide text-slate-300">
            {status?.mode === "vip_direct" ? "VIP DIRECT" : "GROUP"}
          </span>
        </div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="account-next-step">
          <div className="text-xs font-semibold text-slate-400">Langkah berikutnya</div>
          <p className="mt-1 text-sm leading-relaxed text-slate-100">{view.nextStep}</p>
          {view.actionLink && (
            <Link to={view.actionLink.href} className="mt-3 inline-flex min-h-11 items-center rounded-xl bg-pink-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-pink-300">
              {view.actionLink.label}
            </Link>
          )}
          {receivedAt && <div className="mt-3 text-xs text-slate-400">Diperbarui {new Date(receivedAt).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour12: false })} WIB</div>}
        </div>
        {loadFailed && (
          <div className="mt-4 text-sm text-amber-200" role="status" data-testid="status-refresh-error">
            Status belum berhasil diperbarui. {status ? "Yang tampil adalah data terakhir. " : ""}Sistem sedang mencoba mengambil data kembali.
          </div>
        )}
        {isAdmin && status?.last_error && (
          <div className="mt-6 flex items-start gap-2 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            <Warning size={18} className="mt-0.5 shrink-0" />
            <div data-testid="status-error">{status.last_error}</div>
          </div>
        )}
        {status?.verification_url && (
          <div className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm">
            <LinkSimple size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0" data-testid="status-verification">
              <div className="font-semibold mb-1">Verifikasi Diperlukan</div>
              <div className="text-xs mb-3">
                Buka Mini App dan centang Cloudflare bila muncul. Automation akan melanjutkan
                otomatis setelah Telegram mengonfirmasi; gunakan Resume jika konfirmasi belum terdeteksi.
              </div>
              <a
                href={status.verification_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-amber-300 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-amber-200"
                data-testid="verification-link"
              >
                Buka Verifikasi Mini App <LinkSimple size={14} />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" aria-label="Statistik sesi">
        <Metric label="Siklus" value={`#${status?.cycle ?? 0}`} testid="status-cycle" />
        <Metric label="Ikan tertangkap" value={status?.fish_caught ?? 0} testid="status-fish-caught" tone="text-pink-300" />
        <Metric label="Berjalan selama" value={formatSince(status?.started_at)} testid="status-uptime" tone="text-amber-300" />
        <Metric label={view.countdownLabel} value={formatWait(view.remainingSeconds)} testid="status-countdown" tone="text-cyan-300" />
      </div>

      {/* Sub cards */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <SubCard
          icon={<Fish size={20} weight="duotone" />}
          label="Telegram"
          value={!tg ? "Memuat koneksi…" : tg.connected ? tg.display_name || tg.phone || "Terhubung" : "Belum terhubung"}
          sub={!tg ? "Memuat koneksi" : tg.connected ? "Akun tersambung ke layanan" : "Hubungkan kembali dari menu Telegram"}
          ok={tg?.connected}
          testid="sub-telegram"
        />
        <SubCard
          icon={<Waveform size={20} weight="duotone" />}
          label="Sejak Sell Terakhir"
          value={`${status?.fish_since_sell ?? 0} sesi`}
          sub="Extract & jual tiap 3 sesi"
          ok
          testid="sub-fish-since-sell"
        />
        <SubCard
          icon={<Timer size={20} weight="duotone" />}
          label="Action Terakhir"
          value={formatSince(status?.last_action_at)}
          sub={isAdmin ? status?.last_message || "—" : lastMessage}
          ok
          testid="sub-last-action"
        />
      </div>
    </div>
  );
}

function Metric({ label, value, testid, tone = "text-slate-100" }) {
  return (
    <div className="dashboard-metric rounded-2xl p-4 sm:p-5">
      <div className="text-[11px] font-semibold text-slate-400">{label}</div>
      <div className={`mt-2 font-heading text-xl sm:text-2xl font-bold break-words ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function SubCard({ icon, label, value, sub, ok, testid }) {
  return (
    <div className="dashboard-metric rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={ok ? "text-pink-400" : "text-slate-500"}>{icon}</div>
        {ok ? (
          <CheckCircle size={16} className="text-green-500" />
        ) : (
          <Warning size={16} className="text-yellow-500" />
        )}
      </div>
      <div className="text-xs text-slate-400 font-semibold mb-1">{label}</div>
      <div className="font-heading text-base font-bold break-words">{value}</div>
      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{sub}</div>
    </div>
  );
}
